<template>
  <div
    ref="trackRef"
    class="slide-to-confirm"
    :class="{ 'slide-to-confirm--on': armed, 'slide-to-confirm--destructive': destructive }"
    :style="progressStyle"
    :aria-hidden="!armed"
    @pointerdown="onTrackDown"
  >
    <div class="slide-to-confirm__fill" />
    <div class="slide-to-confirm__label">
      <span :id="labelId">{{ label }}</span>
      <i class="pi pi-chevron-right" aria-hidden="true" />
    </div>
    <div
      ref="thumbRef"
      class="slide-to-confirm__thumb"
      role="button"
      :tabindex="armed ? 0 : -1"
      :aria-labelledby="labelId"
      @pointerdown="onDown"
      @pointermove="onMove"
      @pointerup="onUp"
      @pointercancel="onUp"
      @lostpointercapture="onUp"
      @keydown.enter.prevent="onConfirmKey"
      @keydown.space.prevent="onConfirmKey"
    >
      <i class="pi pi-chevron-right" aria-hidden="true" />
    </div>
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref, useId, watch } from 'vue';

// The arm → slide-to-confirm track. Shared by the bid tray, the live-video dock
// and the in-app confirm sheet — between them every path a dealer has to spend
// money on a phone, and the one path that closes their account.
//
// The label is a finished string the parent composes — the bid tray passes
// "Slide to place bid · $24,750", a destructive confirm passes "Slide to close
// my account". It is a superset of the bid-amount shape this used to own, so
// the money formatting lives with the caller that already owns the amount.
//
// One number drives the whole rendering: `progress`, 0 to 1 of the thumb's
// travel, published on the root as a custom property. The fill's width, the
// label's clip mask, the thumb's offset and the fill's visibility are all
// `calc()`ed from it in the <style> block below, which is Sheet's `--sheet-drag`
// idiom and is why nothing here writes an inline style by hand. Cross
// CONFIRM_THRESHOLD and it reports `confirm` and springs back to zero; let go
// short of it and it springs back having reported nothing.
//
// `armed` is the parent's gate on the WHOLE gesture and not merely on the press
// that starts one — disarming mid-drag ends the gesture and returns the track to
// zero, which is how a parent cancels. `resetKey` is the same ending for the
// other reason a gesture in flight is no longer valid: the thing being confirmed
// CHANGED under the thumb. Between them they are the entire contract, and there
// is deliberately no exposed method for either.
//
// The parent owns the placed/confetti/winning overlay; on confirm we reset to 0
// so the track is immediately re-armed for a bidding war.
defineOptions({ name: 'SlideToConfirm' });
const props = defineProps({
  /** The finished track label AND its accessible name — e.g. "Slide to place bid · $24,750". */
  label: { type: String, required: true },
  /** Parent-owned arm gate — the track is invisible and inert until true. */
  armed: { type: Boolean, default: false },
  /**
   * Whatever this track is confirming, as one comparable value — the live bid
   * amount for the bid tray. A CHANGE ends a gesture in flight.
   *
   * It is a key and not the amount itself: the track renders a finished label
   * and does no arithmetic, so what it needs is something it can compare, not
   * something it can read. `null` opts out, which is what a confirm with
   * nothing underneath it to move does (closing an account).
   */
  // The failure it exists for: a dealer is mid-slide at $24,750, the auctioneer
  // takes a bid from the lane, the tray re-prices the slide to $24,850 — and the
  // finger that was already travelling completes a gesture the dealer started
  // against the old number. `armed` cannot catch it, because nothing was
  // canceled; the tray is still armed and still open.
  resetKey: { type: [Number, String], default: null },
  /**
   * Paint the fill, label and thumb in the danger status — for the most
   * permanent slide there is (closing an account). Behavior is unchanged; only
   * the color moves, to the SAME semantic danger tokens ConfirmSheet's danger
   * button reads.
   */
  destructive: { type: Boolean, default: false }
});

const emit = defineEmits(['confirm']);

const trackRef = ref(null);
const thumbRef = ref(null);
const labelId = useId();

// The travel crossed before the gesture counts as a commit. Not exported: it is
// one comparison on the element that owns the gesture, and the tests bound it
// from both sides through the DOM, which asserts the behaviour at the boundary
// rather than the value of a variable.
const CONFIRM_THRESHOLD = 0.9;

// The thumb's geometry, mirrored from the <style> block: a 56px thumb on a 4px
// inset, so the travel is the track less 60px and the finger holds the thumb
// about 30px from its left edge. The two sides are held together by
// `matches the geometry its own stylesheet renders` in SlideToConfirm.test.js.
const THUMB_TRAVEL_INSET = 60;
const THUMB_GRAB_OFFSET = 30;

// How far along the travel the thumb is, 0 to 1. A ref rather than a hand-written
// inline style so that Vue owns the paint and a re-render cannot lose it, and so
// that the resting value is the CSS fallback rather than a second copy of zero —
// which is also what lets a story set the mid-drag state from a decorator.
const progress = ref(0);
const progressStyle = computed(() =>
  (progress.value ? { '--slide-to-confirm-progress': progress.value } : {})
);

// The pointer that owns the gesture, or null when none does. It replaces a
// boolean `dragging`, because the doc comment above has always claimed a SINGLE
// captured pointer and a boolean cannot say which one: without this, a move from
// any second pointer drove the same drag and any second pointer's release ended
// it.
let activePointerId = null;

/**
 * End the gesture, however it ended: released, canceled, disarmed, or confirmed.
 *
 * One function for all four so they cannot drift apart. The capture is released
 * explicitly because a crossing ends the gesture while the finger is still down,
 * and a still-captured thumb goes on swallowing that finger's movement from the
 * page underneath until it lifts.
 */
function endGesture() {
  const pointerId = activePointerId;
  activePointerId = null;
  progress.value = 0;

  // Ordered after the reset above: releasing fires `lostpointercapture`, which
  // is bound to onUp, and onUp compares against an id that is already null.
  if (pointerId !== null && thumbRef.value?.hasPointerCapture?.(pointerId)) {
    thumbRef.value.releasePointerCapture(pointerId);
  }
}

// Disarming is how a parent cancels, and it has to end a gesture that is already
// in flight. Pointer capture keeps delivering moves to a captured element
// whatever `pointer-events` says, so without this a finger that was already down
// when the tray's Cancel button was pressed could carry the thumb across the
// threshold and place the bid that was just canceled.
watch(() => props.armed, (armed) => {
  if (!armed) endGesture();
});

// The amount moving is the other way a gesture in flight stops being the gesture
// the dealer started, and it is the more dangerous of the two: the track stays
// armed, the label re-renders under the thumb, and the travel already behind the
// finger carries over onto the new number. It ends the same way disarming does —
// back to zero, reporting nothing — so the next bid costs a fresh slide.
//
// Deliberately NOT `endGesture` passed straight to `watch`: the watcher hands
// its callback the new and old values, and `endGesture` takes none.
watch(() => props.resetKey, () => { endGesture(); });

// The slider owns any press that lands on it. Once armed it captures the pointer
// (below) and drives the fill off clientX, so the press must NOT also reach the
// Sheet's drag-to-dismiss — that listens on the sheet root and starts on a
// few px of vertical movement, so the natural downward arc of a sideways slide
// would otherwise drag the sheet (and past its threshold, dismiss it) mid-slide.
// Stopping here (the track root, so it covers a press on the thumb OR the track)
// keeps the gesture local to the slider. Grabber/header/body drags still dismiss.
function onTrackDown(ev) {
  if (props.armed) ev.stopPropagation();
}

// The keyboard shortcut for the drag, and deliberately without a threshold:
// somebody who cannot drag gets ONE Enter. Both halves of that sentence are
// guards here, because this route has no threshold to end it the way a crossing
// ends a drag.
//
// `armed`, because `tabindex="-1"` only discourages focus — it does not prevent
// programmatic focus or a dispatched key event, so without it a disarmed slider
// would still place the bid.
//
// `event.repeat`, because holding the key down is ONE deliberate gesture and the
// OS reports it as thirty a second. The drag route cannot double-fire — crossing
// the threshold clears `activePointerId` — and nothing was stopping this one.
// BidSheet is why it matters most: its tray opens armed and `placeBid` closes
// the sheet without ever disarming, so the `armed` guard never trips there.
function onConfirmKey(event) {
  if (!props.armed || event.repeat) return;
  emit('confirm');
}

function onDown(ev) {
  if (!props.armed || activePointerId !== null) return;
  activePointerId = ev.pointerId;
  try { thumbRef.value?.setPointerCapture(ev.pointerId); } catch (_) { /* no active pointer */ }
}

function onMove(ev) {
  // `armed` is re-read here rather than left to the watcher above, so that the
  // guard on the money does not depend on when a watcher happens to flush.
  if (ev.pointerId !== activePointerId || !props.armed || !trackRef.value) return;

  const track = trackRef.value.getBoundingClientRect();
  const travelled = ev.clientX - track.left - THUMB_GRAB_OFFSET;
  const p = Math.max(0, Math.min(1, travelled / (track.width - THUMB_TRAVEL_INSET)));
  progress.value = p;

  if (p > CONFIRM_THRESHOLD) {
    endGesture();
    emit('confirm');
  }
}

function onUp(ev) {
  if (ev.pointerId !== activePointerId) return;
  endGesture();
}

// Unmounting removes the element and takes its listeners with it, so nothing
// here fires afterwards — but the capture this component ASKED for is the one
// thing that is not a listener, and no test in this repository can reach it: a
// synthetic PointerEvent never obtains capture, so a passing test would prove
// only that removed listeners do not fire. Wave 2 asks that whatever is set up
// is torn down in `onUnmounted`, and the honest way to hold something a test
// cannot is to release it explicitly rather than to argue that the browser
// would have.
onUnmounted(endGesture);
</script>

<style scoped>
.slide-to-confirm {
  /* The thumb's geometry, and the one place each of these numbers is written.
     `paint()` used to re-derive all three on every frame from its own copies. */
  --slide-to-confirm-thumb-width: 56px;
  --slide-to-confirm-thumb-inset: 4px;
  --slide-to-confirm-travel: calc(
    100% - var(--slide-to-confirm-thumb-width) - var(--slide-to-confirm-thumb-inset)
  );
  /* Where the fill and the clip mask start: the same lead-in the label is padded
     by, read from the one token that owns it — so a theme moving it moves the
     text, the fill and the mask together instead of sliding them apart. */
  --slide-to-confirm-lead-in: var(--p-slide-to-confirm-label-padding-left);
  /* What the label's TEXT can never have: the lead-in, the gap after the text,
     and the chevron (1rem of primeicons, plus 4px of slack so a rounding does
     not decide this). Subtracted from the track below to size the type. */
  --slide-to-confirm-label-fixed: calc(
    var(--slide-to-confirm-lead-in) + var(--p-slide-to-confirm-label-gap) + 20px
  );
  /* How far along the travel the thumb is. Written by the script during a drag;
     the fallback below IS the resting state, so nothing declares zero twice. */

  position: absolute;
  inset: 0;
  /* The label sizes itself against THIS width (`cqi`, below), so the track has
     to be a query container. Inline-size only: the block size comes from
     `inset: 0` and is nothing the label asks about. */
  container-type: inline-size;
  border-radius: var(--p-slide-to-confirm-border-radius);
  background: var(--p-slide-to-confirm-background);
  border: var(--p-slide-to-confirm-border-width) solid var(--p-slide-to-confirm-border-color);
  overflow: hidden;
  user-select: none;
  opacity: 0;
  pointer-events: none;
  transition: opacity 180ms;
}
.slide-to-confirm--on {
  opacity: 1;
  pointer-events: auto;
}
.slide-to-confirm__fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: calc(
    var(--slide-to-confirm-progress, 0) * var(--slide-to-confirm-travel)
    + var(--slide-to-confirm-lead-in)
  );
  background: var(--p-slide-to-confirm-fill-background);
  /* At rest the fill is a lead-in's worth of colour peeking out around the
     thumb, so it is hidden until the thumb has moved at all. `opacity` clamps to
     [0, 1], and `min()` says the step is deliberate rather than lucky. */
  opacity: min(1, var(--slide-to-confirm-progress, 0) * 1000);
}
.slide-to-confirm__label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--p-slide-to-confirm-label-gap);
  /* The token is the size the label WANTS; the track is what it can pay for.
     `100cqi` IS the track (a `cqi` is a hundredth of the query container's
     content box, and the track declares itself one above), so the second term
     reads: take the room the fixed furniture does not have, and divide it by
     how many times its own font size the longest label measures. Fourteen and
     a half is that figure for "Slide to place bid · $25,550" set in Inter at
     700, measured at every size this produces — it is an upper bound, so the
     line lands inside the track rather than exactly on its edge.
     The copy outgrew a fixed size in #253, when "Slide to confirm" became
     "Slide to place bid": at 17px the longer line no longer fit a 390px phone,
     and with nothing to stop it the label wrapped under its own chevron. The
     `min()` is why nothing got bigger — a wide phone still gets the token.
     Verified from 320px to 430px by the `at phone width` tests in
     SlideToConfirm.test.js. */
  font-size: min(
    var(--p-slide-to-confirm-label-font-size),
    calc((100cqi - var(--slide-to-confirm-label-fixed)) / 14.5)
  );
  font-weight: var(--p-slide-to-confirm-label-font-weight);
  color: var(--p-slide-to-confirm-label-color);
  /* Center the text in the area to the RIGHT of the thumb's lead-in so the first
     character never sits under the thumb (and is never eaten by the resting
     clip). The clip-path below masks from the same origin. */
  padding-left: var(--p-slide-to-confirm-label-padding-left);
  clip-path: inset(0 0 0 calc(
    var(--slide-to-confirm-progress, 0) * var(--slide-to-confirm-travel)
    + var(--slide-to-confirm-lead-in)
  ));
  pointer-events: none;
}
/* One line, whatever the caller composed. `nowrap` is the rule; the ellipsis
   below it is the last resort for a label longer than the shrinking above can
   pay for (a translation, a six-figure price), and `min-width: 0` is what lets
   a flex item shrink far enough to reach it — without it the span refuses to
   go below its text and pushes the chevron off the end. */
.slide-to-confirm__label > span {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
/* The chevron is the only part that must never give up room: it is the whole
   instruction. The text shrinks around it. */
.slide-to-confirm__label > .pi {
  flex: none;
}
.slide-to-confirm__thumb {
  position: absolute;
  top: var(--slide-to-confirm-thumb-inset);
  bottom: var(--slide-to-confirm-thumb-inset);
  left: calc(
    var(--slide-to-confirm-progress, 0) * var(--slide-to-confirm-travel)
    + var(--slide-to-confirm-thumb-inset)
  );
  width: var(--slide-to-confirm-thumb-width);
  border-radius: var(--p-slide-to-confirm-thumb-border-radius);
  background: var(--p-slide-to-confirm-thumb-background);
  color: var(--p-slide-to-confirm-thumb-color);
  display: grid;
  place-items: center;
  cursor: grab;
  touch-action: none;
  box-shadow: var(--p-slide-to-confirm-thumb-box-shadow);
  font-size: var(--p-slide-to-confirm-thumb-font-size);
}
.slide-to-confirm__thumb:active { cursor: grabbing; }

/* The destructive skin. Only the paint moves — same geometry, same behavior —
   so this overrides the fill, label and thumb color with the component's own
   danger tokens (which the preset points at the semantic danger status). */
.slide-to-confirm--destructive .slide-to-confirm__fill {
  background: var(--p-slide-to-confirm-destructive-fill-background);
}
.slide-to-confirm--destructive .slide-to-confirm__label {
  color: var(--p-slide-to-confirm-destructive-label-color);
}
.slide-to-confirm--destructive .slide-to-confirm__thumb {
  background: var(--p-slide-to-confirm-destructive-thumb-background);
  color: var(--p-slide-to-confirm-destructive-thumb-color);
}
</style>
